import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Badge,
  Box,
  Button,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { Add, FilterList, ViewColumn } from "@mui/icons-material";
import { CaseTitleBarMenu } from "../../components/titleBarMenu/CaseTitleBarMenu";
import {
  DataGridPro,
  gridClasses,
  Toolbar,
  ToolbarButton,
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import "../AppPage.css";
import { useNavigate } from "react-router-dom";
import { ContentTitleBar } from "../../components/ContentTitleBar";
import { fullCaseReset } from "../../App";
import { WaitDialog } from "../../components/dialogs";
import {
  formatDateFromDateString,
  formatDateRelative,
} from "./utils/formatDate";
import { getNavigationUrl } from "../../store/navigationUtil";
import { loadCases } from "./utils/dataLoader";
import {
  animationDuration,
  animationKeyFramesGrow,
  animationKeyFramesPulse,
  animationTiming,
} from "./utils/animation";
import { useAuth } from "../../store/auth/hooks";
import {
  buildDriveUsageRequestCase,
  CASE_SCOPE,
  DRIVE_USAGE,
  DRIVE_USAGE_TIMEOUT_MS,
  driveUsageBytes,
} from "./utils/driveUsage";
import useWebSocket from "react-use-websocket";
import { WEBSOCKET_URL } from "../../store/config";
import { LoadingDots } from "../../components/LoadingDots";
import { humanFileSize } from "../../store/formatUtil";

export default function CasesPage() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const [cases, setCases] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: "include",
    ids: new Set(),
  });
  // Check if datagrid is sorted to prevent animation running on sort
  const [isSorted, setIsSorted] = useState(false);
  // ID of element selected for editing to be able to compare to next render
  // of datagrid
  const [prevId, setPrevId] = useState();
  // Index in rendered grid of element selected for editing
  const [prevIndex, setPrevIndex] = useState();
  // Check to not display grid once loaded until indices can be compared
  // to determine if index has changed
  const [comparingIndices, setComparingIndices] = useState(false);
  // Type of animation is determined by whether an element has changed
  // position and has a different index than the previous render
  const [isNewIndex, setIsNewIndex] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState();
  // Most recently updated case (using this approach in case the default sort should change
  // in future updates)
  const lastModifiedRow = cases.reduce(
    (acc, curr) => (curr.updatedEpochMs > acc.updatedEpochMs ? curr : acc),
    cases[0],
  );
  const { accessCode } = useAuth();
  const [websocketRegistered, setWebsocketRegistered] = useState(false);
  // On-disk size per clip, keyed by clip id. Kept apart from caseClips because
  // a CLIP_STATUS message reloads that list wholesale, which would throw the
  // measurements away.
  const [driveUsage, setDriveUsage] = useState({});
  const [measuringDriveUsage, setMeasuringDriveUsage] = useState(false);
  // The correlation id of the batch we asked for. Drive usage messages fan out
  // to every session this user has open, so this is the only way to tell our
  // own batch's terminator from another tab's.
  const driveUsageCorrelationId = useRef(null);

  const CustomToolbar = () => {
    return (
      columnVisibilityModel && (
        <Toolbar>
          <Tooltip title="Filters">
            <FilterPanelTrigger
              sx={{
                color: theme.palette.primary.dark,
              }}
              render={(props, state) => (
                <ToolbarButton {...props} color="default">
                  <Badge badgeContent={state.filterCount} color="primary">
                    <FilterList fontSize="small" />
                  </Badge>
                </ToolbarButton>
              )}
            />
          </Tooltip>
          <Tooltip title="Columns">
            <ColumnsPanelTrigger
              sx={{
                color: theme.palette.primary.dark,
              }}
              render={<ToolbarButton />}
            >
              <ViewColumn fontSize="small" />
            </ColumnsPanelTrigger>
          </Tooltip>
        </Toolbar>
      )
    );
  };

  const columns = [
    {
      field: "caseName",
      headerName: "Case",
      flex: 2.5,
      cellClassName: "columnHeaderLarge",
      hideable: false,
    },
    {
      field: "numberOfClips",
      headerName: "Clips",
      flex: 0.75,
    },
    {
      field: "fieldOffice",
      headerName: "Field Office",
      flex: 1.25,
    },
    {
      field: "description",
      headerName: "Case Description",
      flex: 2.5,
    },
    {
      field: "caseFileNumber",
      headerName: "Case File #",
      flex: 1,
    },
    {
      field: "createdDate",
      headerName: "Date Created",
      flex: 1,
      valueGetter: (_value, row) => new Date(row.createdEpochMs),
      valueFormatter: (value) => formatDateFromDateString(value.getTime()),
      type: "date",
    },
    {
      field: "createdBy",
      headerName: "Created by",
      flex: 1,
      valueGetter: (_value, row) => row.createdBy.name,
    },
    {
      field: "viewers",
      headerName: "Viewers",
      flex: 1,
      filterable: false,
      valueGetter: (_value, row) => formatViewers(row),
    },
    {
      field: "updatedEpochMs",
      headerName: "Last Modified",
      flex: 1.25,
      cellClassName: "cellEllipsis",
      valueGetter: (value) => new Date(value),
      valueFormatter: (value) => formatDateRelative(value.getTime()),
      type: "date",
    },
    {
      field: "driveUsage",
      headerName: "Drive Usage",
      flex: 1,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height={1}
          width={1}
        >
          {value === undefined ? (
            // Still measuring, or the request never came back.
            measuringDriveUsage ? (
              <LoadingDots />
            ) : (
              <Typography variant="body2">&mdash;</Typography>
            )
          ) : (
            <Typography variant="body2">
              {value === null ? "—" : humanFileSize(value, true)}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "Actions",
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      width: 44,
      renderHeader: () => null,
      renderCell: ({ row }) => {
        return (
          <Box
            display="flex"
            justifyContent="center"
            width={1}
            height={1}
            alignItems="center"
          >
            <CaseTitleBarMenu
              af_case={row}
              onChange={dataChanged}
              readOnly={!row.editable}
            />
          </Box>
        );
      },
    },
  ];

  const formatViewers = (r) => {
    if (r.viewAll) return "ALL";
    return r.viewers;
  };

  const gridAnimationStates = useCallback(() => {
    setRowSelectionModel({ type: "include", ids: new Set() });
    setIsNewIndex(
      apiRef.current?.state?.sorting?.sortedRows[prevIndex] !== prevId,
    );
    if (Boolean(cases.length)) {
      setColumnVisibilityModel({
        caseFileNumber: Boolean(
          cases.map((item) => item.caseFileNumber).filter(Boolean).length,
        ),
        description: Boolean(
          cases.map((item) => item.description).filter(Boolean).length,
        ),
        fieldOffice: Boolean(
          cases.map((item) => item.fieldOffice).filter(Boolean).length,
        ),
      });
    }
    setComparingIndices(false);
    setIsSorted(false);
  }, [cases, prevId, prevIndex, apiRef]);

  const gridAnimationClasses = (id) => {
    if (
      !isSorted &&
      id === lastModifiedRow?.id &&
      Math.floor((Date.now() - lastModifiedRow?.updatedEpochMs) / 1000) < 5
    ) {
      if (isNewIndex) {
        return "growRowAnimate";
      } else if (!isNewIndex && Boolean(![...rowSelectionModel.ids]?.length)) {
        return "pulseRowAnimate";
      }
    }
  };

  const dataChanged = () => {
    load();
  };

  const load = useCallback(async () => {
    const currCases = await loadCases();
    if (!currCases) {
      // login errors
      return;
    }
    setCases(currCases);
    setComparingIndices(true);
    setLoaded(true);
  }, []);

  const handleCellClick = (e) => {
    if (e.field !== "Actions") {
      navigate(getNavigationUrl(e.row));
    }
  };

  /**
   * Drive usage arrives one message per clip, then a terminator carrying
   * `complete: true`. Ordering is guaranteed server-side, so the terminator
   * can't overtake the results it closes out.
   */
  const handleDriveUsageMessage = (payload) => {
    if (!payload) return;
    const { correlationId, usage, complete, error } = payload;

    // Take any clip measurement, even from a correlation id we never issued:
    // the server also pushes updates unprompted when something changes on
    // disk, which saves the client from having to know which clip statuses
    // affect footprint. Clip ids are unique across cases, so a message meant
    // for another tab's case can't land on one of our rows.

    // TODO: change scope to user or case
    if (usage && usage.scope === CASE_SCOPE) {
      setDriveUsage((prev) => ({ ...prev, [usage.id]: usage }));
    }

    // Only our own batch drives the loading state.
    if (!correlationId || correlationId !== driveUsageCorrelationId.current) {
      return;
    }
    if (error) {
      console.error("[CasesPage][WebSocket] Drive usage error:", error);
    }
    if (complete) {
      driveUsageCorrelationId.current = null;
      setMeasuringDriveUsage(false);
    }
  };

  const { sendMessage } = useWebSocket(WEBSOCKET_URL, {
    share: true,
    onOpen: () => {
      console.log("WebSocket connection established.", WEBSOCKET_URL);
    },
    onMessage: (event) => {
      if (event && event.data) {
        if (event.data === "pong") return;
        const data = JSON.parse(event.data);
        if (data.command === DRIVE_USAGE) {
          handleDriveUsageMessage(data.data);
        }
      }
    },
  });

  useEffect(() => {
    if (!websocketRegistered) {
      setWebsocketRegistered(true);
      const payload = {
        command: "REGISTER",
        authorization: accessCode,
      };
      sendMessage(JSON.stringify(payload));
    }
  }, [accessCode, websocketRegistered, sendMessage]);

  /**
   * Ask for drive usage once per user, after REGISTER has gone out -- only
   * registered sessions are in the server's cache, and answers to an
   * unregistered session go nowhere. This effect runs a render later than the
   * registration effect above, so the ordering holds.
   */
  useEffect(() => {
    if (!websocketRegistered || !cases.length) return;

    const correlationId = crypto.randomUUID();
    driveUsageCorrelationId.current = correlationId;
    setDriveUsage({});
    setMeasuringDriveUsage(true);
    // NOTE: for user and case loop over each user/case and send a request for each user/case combination
    cases.forEach((c) => {
      sendMessage(buildDriveUsageRequestCase(accessCode, c.id, correlationId));
    });

    // A payload the server can't parse is dropped without a reply, so stop
    // waiting rather than animating forever.
    const timeout = setTimeout(() => {
      if (driveUsageCorrelationId.current === correlationId) {
        console.warn("[CasesPage] Drive usage request timed out");
        driveUsageCorrelationId.current = null;
        setMeasuringDriveUsage(false);
      }
    }, DRIVE_USAGE_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [accessCode, cases, websocketRegistered, sendMessage]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    gridAnimationStates();
  }, [cases]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleStateChange = (params) => {
      setPrevId([...rowSelectionModel.ids][0]);
      setPrevIndex(
        params.sorting.sortedRows.indexOf([...rowSelectionModel.ids][0]),
      );
    };

    return apiRef.current.subscribeEvent("stateChange", handleStateChange);
  }, [apiRef, rowSelectionModel]);

  const rows = useMemo(
    () =>
      cases.map((c) => ({
        ...c,
        driveUsage: driveUsageBytes(driveUsage[c.id]),
      })),
    [cases, driveUsage],
  );

  return (
    <Stack component="main" height={1}>
      <WaitDialog open={!loaded || comparingIndices} />
      <Stack flex={1} p={2}>
        <Stack height="calc(100vh - 222px)" maxHeight="calc(100vh - 222px)">
          <DataGridPro
            apiRef={apiRef}
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            disableColumnMenu
            getRowClassName={({ id }) => gridAnimationClasses(id)}
            hideFooter
            hideFooterPagination
            isCellEditable={() => {
              return false;
            }}
            initialState={{
              columns: {
                columnVisibilityModel: columns.reduce(
                  (acc, curr) => ({ ...acc, [curr.field]: false }),
                  {},
                ),
              },
              sorting: {
                sortModel: [{ field: "updatedEpochMs", sort: "desc" }],
              },
            }}
            loading={!loaded || comparingIndices}
            onCellClick={handleCellClick}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            onRowSelectionModelChange={(newRowSelectionModel) =>
              setRowSelectionModel(newRowSelectionModel)
            }
            onSortModelChange={() => setIsSorted(true)}
            rows={rows}
            rowSelectionModel={rowSelectionModel}
            showToolbar
            slots={{
              toolbar: CustomToolbar,
            }}
            slotProps={{
              columnsManagement: {
                disableResetButton: true,
              },
            }}
            sx={{
              display: "flex",
              border: "none",
              "& .MuiDataGrid-iconButtonContainer": {
                marginLeft: "2px",
                visibility: "visible !important",
                width: "auto !important",
              },
              ".MuiDataGrid-row.Mui-selected": {
                backgroundColor: "rgba(25, 83, 183, 0.1)",
              },
              ".MuiDataGrid-row.MuiDataGrid-row--lastVisible": {
                borderBottom: "1px solid rgba(25, 83, 183, 0.1)",
              },
              "& .MuiDataGrid-filler > div:first-of-type": {
                borderTop: "none",
              },
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-row.Mui-selected:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
              [`.${gridClasses.row}.growRowAnimate`]: {
                animation: `${animationKeyFramesGrow} ${animationDuration} ${animationTiming}`,
              },
              [`.${gridClasses.row}.pulseRowAnimate`]: {
                animation: `${animationKeyFramesPulse} ${animationDuration} ${animationTiming}`,
              },
            }}
          />
        </Stack>
      </Stack>
      <Paper
        elevation={4}
        sx={{
          p: 1,
          mx: 1,
          mb: 1,
        }}
      >
        <Button
          aria-label="Add Case"
          variant="contained"
          startIcon={<Add />}
          onClick={async () => {
            await fullCaseReset(dispatch);
            navigate("/app/addCase");
          }}
        >
          New Case
        </Button>
      </Paper>
      <ContentTitleBar label="Cases" />
    </Stack>
  );
}
